import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  FileText,
  Search,
  RefreshCw,
  ArrowLeft,
  Trash2,
  AlertTriangle,
  Info,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function AdminLogs() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cnpjFilter, setCnpjFilter] = useState("");
  const [nivelFilter, setNivelFilter] = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [diasLimpar, setDiasLimpar] = useState(30);
  const [clearing, setClearing] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("adminToken");
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }, []);

  const fetchLogs = useCallback(
    async (showRefresh = false) => {
      if (showRefresh) setRefreshing(true);

      try {
        const params = new URLSearchParams({
          page: pagination.page.toString(),
          limit: pagination.limit.toString(),
        });
        if (cnpjFilter) params.append("cnpj", cnpjFilter);
        if (nivelFilter && nivelFilter !== "todos")
          params.append("nivel", nivelFilter);
        if (dataInicio) params.append("data_inicio", dataInicio);
        if (dataFim) params.append("data_fim", dataFim);

        const response = await fetch(
          `${API_URL}/api/admin-panel/logs?${params}`,
          {
            headers: getAuthHeaders(),
          },
        );

        if (response.status === 401) {
          localStorage.removeItem("adminToken");
          navigate("/admin");
          return;
        }

        const data = await response.json();
        if (data.success) {
          setLogs(data.data.logs);
          setPagination((prev) => ({ ...prev, ...data.data.pagination }));
        }
      } catch (error) {
        console.error("Erro ao buscar logs:", error);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [
      getAuthHeaders,
      navigate,
      pagination.page,
      pagination.limit,
      cnpjFilter,
      nivelFilter,
      dataInicio,
      dataFim,
    ],
  );

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchLogs();
  }, [navigate, fetchLogs]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchLogs();
  };

  const handleClearLogs = async () => {
    setClearing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin-panel/logs/clear`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ dias: diasLimpar }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Erro ao limpar logs");
      }

      setShowClearDialog(false);
      fetchLogs(true);
      alert(data.message);
    } catch (error) {
      alert(error.message);
    } finally {
      setClearing(false);
    }
  };

  const getNivelBadge = (nivel) => {
    switch (nivel?.toLowerCase()) {
      case "erro":
      case "error":
        return (
          <Badge className="bg-red-100 text-red-700">
            <AlertCircle className="w-3 h-3 mr-1" />
            ERRO
          </Badge>
        );
      case "aviso":
      case "warning":
        return (
          <Badge className="bg-amber-100 text-amber-700">
            <AlertTriangle className="w-3 h-3 mr-1" />
            AVISO
          </Badge>
        );
      case "info":
      default:
        return (
          <Badge className="bg-blue-100 text-blue-700">
            <Info className="w-3 h-3 mr-1" />
            INFO
          </Badge>
        );
    }
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-logs">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/admin/dashboard")}
                className="text-slate-500 hover:text-blue-600"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
              <div className="h-6 w-px bg-slate-300" />
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-amber-600" />
                <h1 className="text-sm sm:text-lg font-semibold text-slate-800">
                  Logs do Sistema
                </h1>
              </div>
            </div>

            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowClearDialog(true)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="clear-logs-button"
            >
              <Trash2 className="w-4 h-4 sm:mr-2" />
              <p className="hidden sm:flex">Limpar Logs Antigos</p>
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <Card className="bg-white border-slate-200 mb-6 shadow-sm">
          <CardContent className="pt-6">
            <form
              onSubmit={handleSearch}
              className="grid grid-cols-1 sm:grid-cols-3 xl:grid-cols-6 gap-4"
            >
              <div className="md:col-span-2">
                <Label className="text-slate-600 text-xs mb-1 block">
                  CNPJ da Empresa
                </Label>
                <Input
                  placeholder="Filtrar por CNPJ..."
                  value={cnpjFilter}
                  onChange={(e) => setCnpjFilter(e.target.value)}
                  className="bg-white border-slate-300"
                  data-testid="filter-cnpj"
                />
              </div>
              <div>
                <Label className="text-slate-600 text-xs mb-1 block">
                  Nível
                </Label>
                <Select value={nivelFilter} onValueChange={setNivelFilter}>
                  <SelectTrigger
                    className="bg-white border-slate-300"
                    data-testid="filter-nivel"
                  >
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="erro">Erro</SelectItem>
                    <SelectItem value="aviso">Aviso</SelectItem>
                    <SelectItem value="info">Info</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-600 text-xs mb-1 block">
                  Data Início
                </Label>
                <Input
                  type="date"
                  value={dataInicio}
                  onChange={(e) => setDataInicio(e.target.value)}
                  className="bg-white border-slate-300"
                  data-testid="filter-data-inicio"
                />
              </div>
              <div>
                <Label className="text-slate-600 text-xs mb-1 block">
                  Data Fim
                </Label>
                <Input
                  type="date"
                  value={dataFim}
                  onChange={(e) => setDataFim(e.target.value)}
                  className="bg-white border-slate-300"
                  data-testid="filter-data-fim"
                />
              </div>
              <div className="flex items-end gap-2">
                <Button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="search-logs"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Buscar
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fetchLogs(true)}
                  disabled={refreshing}
                  className="border-slate-300"
                  data-testid="refresh-logs"
                >
                  <RefreshCw
                    className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
                  />
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Nenhum log encontrado</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="logs-table">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left p-4 text-slate-600 font-medium w-40">
                        Data/Hora
                      </th>
                      <th className="text-left p-4 text-slate-600 font-medium w-24">
                        Nível
                      </th>
                      <th className="text-left p-4 text-slate-600 font-medium w-40">
                        Empresa
                      </th>
                      <th className="text-left p-4 text-slate-600 font-medium">
                        Mensagem
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr
                        key={log._id}
                        className="border-b border-slate-100 hover:bg-slate-50"
                        data-testid={`log-row-${log._id}`}
                      >
                        <td className="p-4 text-slate-600 text-sm font-mono">
                          {new Date(log.createdAt).toLocaleString("pt-BR")}
                        </td>
                        <td className="p-4">{getNivelBadge(log.nivel)}</td>
                        <td className="p-4">
                          {log.empresa_id ? (
                            <div>
                              <p className="text-slate-800 text-sm">
                                {log.empresa_id.nome_empresa}
                              </p>
                              <p className="text-slate-500 text-xs font-mono">
                                {log.empresa_id.cnpj}
                              </p>
                            </div>
                          ) : (
                            <span className="text-slate-400">Sistema</span>
                          )}
                        </td>
                        <td className="p-4">
                          <p className="text-slate-800 text-sm">{log.acao}</p>
                          {log.detalhes && (
                            <pre className="text-slate-500 text-xs mt-1 bg-slate-50 p-2 rounded overflow-x-auto max-w-xl">
                              {typeof log.detalhes === "object"
                                ? JSON.stringify(log.detalhes, null, 2)
                                : log.detalhes}
                            </pre>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  Mostrando {logs.length} de {pagination.total} logs
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page - 1,
                      }))
                    }
                    disabled={pagination.page === 1}
                    className="border-slate-300"
                    data-testid="prev-page-logs"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-slate-500">
                    Página {pagination.page} de {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page + 1,
                      }))
                    }
                    disabled={pagination.page === pagination.totalPages}
                    className="border-slate-300"
                    data-testid="next-page-logs"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Clear Logs Dialog */}
      <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <AlertDialogContent
          className="bg-white border-slate-200"
          data-testid="clear-logs-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-800">
              Limpar Logs Antigos
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              Esta ação irá remover permanentemente todos os logs mais antigos
              que o período selecionado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label className="text-slate-700">
              Remover logs mais antigos que:
            </Label>
            <Select
              value={diasLimpar.toString()}
              onValueChange={(v) => setDiasLimpar(parseInt(v))}
            >
              <SelectTrigger
                className="mt-2 bg-white border-slate-300"
                data-testid="clear-days-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                <SelectItem value="7">7 dias</SelectItem>
                <SelectItem value="15">15 dias</SelectItem>
                <SelectItem value="30">30 dias</SelectItem>
                <SelectItem value="60">60 dias</SelectItem>
                <SelectItem value="90">90 dias</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white border-slate-300 text-slate-700 hover:bg-slate-50">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearLogs}
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={clearing}
              data-testid="confirm-clear-logs"
            >
              {clearing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Limpar Logs
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
